import { useLayoutEffect, useState, type RefObject } from 'react';
import { textareaFit } from './textareaFit';

// The nearest ancestor that clips or scrolls: its size is the room the textarea has.
function clippingAncestorOf(el: HTMLElement): HTMLElement | null {
    for (let node = el.parentElement; node; node = node.parentElement) {
        if (getComputedStyle(node).overflowY !== 'visible') return node;
    }
    return null;
}

function fitTextarea(el: HTMLTextAreaElement, maxHeightPx: number): void {
    const container = clippingAncestorOf(el);
    const keptScrollTop = container?.scrollTop ?? 0;
    el.style.height = 'auto';
    const { heightPx, overflowY } = textareaFit(el.scrollHeight, maxHeightPx);
    el.style.height = `${heightPx}px`;
    if (el.style.overflowY !== overflowY) el.style.overflowY = overflowY;
    // Measuring at height auto briefly shrinks the content, which clamps a scroller around it.
    if (container && container.scrollTop !== keptScrollTop) container.scrollTop = keptScrollTop;
}

// Refits when the room changes (banner, window resize) so a fill height is never left pinned.
function refitOnResize(el: HTMLTextAreaElement, maxHeightPx: number): () => void {
    const room = clippingAncestorOf(el) ?? el.parentElement;
    if (!room) return () => undefined;
    let frame = 0;
    const observer = new ResizeObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => fitTextarea(el, maxHeightPx));
    });
    observer.observe(room);
    return () => {
        observer.disconnect();
        cancelAnimationFrame(frame);
    };
}

// Grows the textarea with its text up to maxHeightPx, then scrolls inside; shrinks back when text goes.
export function useAutoGrowTextarea(
    ref: RefObject<HTMLTextAreaElement>,
    value: string,
    maxHeightPx: number
): void {
    const [textarea, setTextarea] = useState<HTMLTextAreaElement | null>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        fitTextarea(el, maxHeightPx);
        // Watched from the first fit, since only a fit pins a height that can go stale.
        setTextarea(el);
    }, [ref, value, maxHeightPx]);

    useLayoutEffect(
        () => (textarea ? refitOnResize(textarea, maxHeightPx) : undefined),
        [textarea, maxHeightPx]
    );
}
